import {createStyles, Group, Stack, Text, Tooltip} from '@mantine/core';
import classNames from 'classnames';
import gql from 'graphql-tag';
import React, {useMemo} from 'react';

import {AsyncTaskStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import getTestProps from 'test/lib/cypress/getTestProps';
import Badge, {BadgeColor} from 'ui-library/Badge';
import * as nyc from 'ui-library/NYC';
import sanitizeLink from 'util/sanitizeLink';

import {
  RecentAsyncTasksQuery,
  RecentAsyncTasksQueryVariables,
  RecentAsyncTasksQuery_recentAsyncTasks_AsyncTaskNode as RecentAsyncTaskNode,
} from './__generated/RecentAsyncTasksQuery';
import useRecentActivityPolling from './useRecentActivityPolling';

// Client-side defaults for the recent activity window and page size. These are independent from the
// backend resolver's defaults — the frontend owns the UX decision of how much data this sidepanel
// surface should show, and should be able to change it without a backend change.
const RECENT_ACTIVITY_LOOKBACK_DAYS = 7;
const RECENT_ACTIVITY_LIMIT = 10;

export const RECENT_ASYNC_TASKS_QUERY = gql`
  query RecentAsyncTasksQuery($lookbackDays: StrictInt!, $limit: StrictInt!)
  @skipAudit
  @engComponent(name: NAVIGATION) {
    recentAsyncTasks(lookbackDays: $lookbackDays, limit: $limit) {
      id
      status
      displayName
      detailsUrl
      createdAt
      progress {
        totalCount
      }
    }
  }
`;

export const DEFAULT_POLL_INTERVAL_MS = 3000;

interface StatusDisplay {
  label: string;
  sortOrder: number;
  color: BadgeColor;
}

// Order: in-progress first (user is actively waiting), then failures (actionable), then completed.
// Colors are ui-library BadgeColor variants (see ui-library/Badge).
const STATUS_DISPLAY: Record<AsyncTaskStatus, StatusDisplay> = {
  [AsyncTaskStatus.IN_PROGRESS]: {
    label: 'In progress',
    sortOrder: 0,
    color: BadgeColor.COLLECTIBLE_BLUE,
  },
  [AsyncTaskStatus.FAILED]: {
    label: 'Failed',
    sortOrder: 1,
    color: BadgeColor.RED,
  },
  [AsyncTaskStatus.SUCCEEDED]: {
    label: 'Completed',
    sortOrder: 2,
    color: BadgeColor.GREEN_FILLED,
  },
};

// Fallback used when the server returns a status enum value that this client version doesn't know about.
// This keeps the component from crashing on schema extensions and keeps unknown statuses sorted last.
const UNKNOWN_STATUS_DISPLAY: StatusDisplay = {
  label: 'Unknown',
  sortOrder: Number.MAX_SAFE_INTEGER,
  color: BadgeColor.GRAY,
};

function getStatusDisplay(status: AsyncTaskStatus): StatusDisplay {
  return STATUS_DISPLAY[status] ?? UNKNOWN_STATUS_DISPLAY;
}

function StatusBadge({status}: {status: AsyncTaskStatus}): JSX.Element {
  const {label, color} = getStatusDisplay(status);
  return (
    <Badge
      color={color}
      bold={true}
      dataTestAttrs={{component: 'RecentActivitySection', element: 'statusBadge', key: status}}
    >
      {label}
    </Badge>
  );
}

interface TaskRowProps {
  task: RecentAsyncTaskNode;
}

function TaskRow({task}: TaskRowProps): JSX.Element {
  const {classes} = useRowStyles();
  const name = task.displayName ?? task.id;
  // sanitizeLink rejects unsafe protocols (e.g. `javascript:`), missing/invalid TLDs, etc., so we
  // never wire a focusable <a> to something a malicious resolver could weaponize.
  const safeDetailsUrl = task.detailsUrl != null ? sanitizeLink(task.detailsUrl) : null;

  // Wrap the focusable element directly so keyboard users get the tooltip when truncation hides the
  // full name. When there's no link we render a plain <span> (not focusable) since there's nothing
  // to act on; hover still reveals the tooltip for mouse users. The inner `[name] (count)` is split
  // into three spans so that the trailing `]` and count remain visible when the name overflows: e.g.
  // "[Foo bar baz...] (123)".
  return (
    <Group
      position="apart"
      noWrap={true}
      className={classes.row}
      {...getTestProps({component: 'RecentActivitySection', element: 'taskRow', key: task.id})}
    >
      <Tooltip label={name} multiline={true} withArrow={true} position="top-start" withinPortal={true}>
        <Text
          component={safeDetailsUrl != null ? 'a' : 'span'}
          {...(safeDetailsUrl != null ? {href: safeDetailsUrl} : {})}
          className={classNames(classes.taskNameWrapper, {whiteLink: safeDetailsUrl != null})}
          {...getTestProps({
            component: 'RecentActivitySection',
            element: safeDetailsUrl != null ? 'taskLink' : 'taskName',
            key: task.id,
          })}
        >
          <Text component="span" className={classes.taskNameFixed}>
            [
          </Text>
          <Text component="span" className={classes.taskNameInner}>
            {name}
          </Text>
          <Text component="span" className={classes.taskNameFixed}>{`] (${task.progress.totalCount})`}</Text>
        </Text>
      </Tooltip>
      <StatusBadge status={task.status} />
    </Group>
  );
}

export interface RecentActivitySectionProps {
  /**
   * Polling interval (ms) to use while any task is in progress. Exposed as a prop so tests can pass
   * a small value without reaching into module-level state. Defaults to {@link DEFAULT_POLL_INTERVAL_MS}.
   */
  pollIntervalMs?: number;
}

const EMPTY_TASKS: ReadonlyArray<RecentAsyncTaskNode> = [];

export default function RecentActivitySection({
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: RecentActivitySectionProps = {}): JSX.Element | null {
  const {classes} = useSectionStyles();
  const queryResult = useBenchlingQuery<RecentAsyncTasksQuery, RecentAsyncTasksQueryVariables>(
    RECENT_ASYNC_TASKS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
      variables: {
        lookbackDays: RECENT_ACTIVITY_LOOKBACK_DAYS,
        limit: RECENT_ACTIVITY_LIMIT,
      },
    }
  );

  const recentAsyncTasks =
    queryResult.type === QueryResultType.SUCCESS ? queryResult.data.recentAsyncTasks : EMPTY_TASKS;

  // Narrow the memo's deps to the underlying list so we don't invalidate on every Apollo re-render.
  const tasks = useMemo(
    () =>
      [...recentAsyncTasks].sort((a, b) => {
        const statusDiff = getStatusDisplay(a.status).sortOrder - getStatusDisplay(b.status).sortOrder;
        if (statusDiff !== 0) {
          return statusDiff;
        }
        return b.createdAt.localeCompare(a.createdAt);
      }),
    [recentAsyncTasks]
  );

  const hasInProgress = useMemo(
    () => tasks.some((task) => task.status === AsyncTaskStatus.IN_PROGRESS),
    [tasks]
  );

  useRecentActivityPolling({
    hasInProgress,
    pollIntervalMs,
    startPolling: queryResult.startPolling,
    stopPolling: queryResult.stopPolling,
    refetch: queryResult.refetch,
  });

  // On a hard error, drop the section (matches behavior of neighboring home-sidepanel sections).
  if (queryResult.type === QueryResultType.ERROR) {
    return null;
  }

  // Avoid flashing the empty state during the initial query: render no body content until we have
  // a SUCCESS result. (Subsequent refetches keep the prior data, so they don't re-enter PENDING.)
  return (
    <nyc.Row>
      <nyc.Container
        size="sidepanel"
        className="homeSidepanel-today-itemSummary"
        {...getTestProps({component: 'RecentActivitySection', element: 'root'})}
      >
        <nyc.Row className="homeSidepanel-today-sectionHeader">
          <div className="homeSidepanel-today-sectionHeader-content">Recent Activity</div>
        </nyc.Row>
        {queryResult.type === QueryResultType.PENDING ? null : tasks.length > 0 ? (
          <Stack spacing={0}>
            {tasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </Stack>
        ) : (
          <div
            className={classes.emptyState}
            {...getTestProps({component: 'RecentActivitySection', element: 'emptyState'})}
          >
            Processes that take time to complete will appear here
          </div>
        )}
      </nyc.Container>
    </nyc.Row>
  );
}

const useRowStyles = createStyles((theme) => ({
  row: {
    padding: `${theme.spacing.xs} 0`,
  },
  taskNameWrapper: {
    alignItems: 'baseline',
    color: 'inherit',
    display: 'flex',
    flex: 1,
    marginRight: theme.spacing.sm,
    minWidth: 0,
    overflow: 'hidden',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  taskNameInner: {
    color: 'inherit',
    fontSize: theme.fontSizes.lg,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Brackets and the trailing `(count)` never shrink, so they stay attached to the truncated name
  // regardless of available width.
  taskNameFixed: {
    color: 'inherit',
    flexShrink: 0,
    fontSize: theme.fontSizes.lg,
    whiteSpace: 'pre',
  },
}));

const useSectionStyles = createStyles((theme) => ({
  emptyState: {
    fontSize: theme.fontSizes.lg,
    fontStyle: 'italic',
    opacity: 0.8,
    padding: `${theme.spacing.xs} 0`,
  },
}));
